import { handleError, MessageBoxButton, useNetwork, userInteraction } from "@eusoft/webapp-framework";
import { forModel } from "@eusoft/webapp-jsx";
import { Content, contentInfo, enumItemsSource, formatCurrency, formatText, MaterialIcon, Message, type ObjectEditor, objectEditor, range, required, SingleSelectorTemplates, staticItemsSourceWithIcon, type IContentInfo } from "@eusoft/webapp-ui";
import { paymentService } from "../../abstraction/IPaymentService";
import { MarkDown } from "../../components/MarkDown";
import { OrderItemType, OrderType, PaymentCardStatus, PaymentMethod, RefundCardReason, SalePointType, type IPaymentCardInfo } from "../../entities/Commands";
import { Authorize } from "../../services/Authorize";
import { context } from "../../services/Context";
import { executeAsync } from "../../services/DataService";
import { withSignManager } from "../../services/SignManager";
import { WithContext } from "../../services/WithContext";
import { registerComponent } from "@eusoft/webapp-core/Component";

export interface IRefundCardPageArgs {
    cardCode: string;
}

interface IRefundData {
    amount: number;
    reason: RefundCardReason;
    paymentMethod: PaymentMethod;
    providerTransId: string;
    note: string;
    reset: boolean;
    close: boolean;
    allAmount: boolean;
}

export class RefundCardPage extends Content<IRefundCardPageArgs> {

    constructor() {

        super();

        this.init(RefundCardPage, {
            title: "refund-card",
            style: ["panel"],
            actions: [{
                name: "refund",
                text: "refund-action", 
                executeAsync: () => this.refundAsync(),
                type: "global"
            }
            ],
            body: forModel(this, m => <>
                {(m.info && m.info.balance != m.info.refundableBalance) && <Message type="warning">
                    {formatText("msg-balance-refundable-different", formatCurrency(m.info.balance), formatCurrency(m.info.refundableBalance))}
                </Message>}
                {m.editor = objectEditor(bld => <>
                    {bld.boolean(a => a.allAmount, {
                        label: "all-amount",
                        onChanged: (data, v) => {
                            if (v)
                                data.amount = this.info.refundableBalance;
                        }
                    })}
                    {bld.number(a => a.amount, {
                        validators: [required, range(0.01, this.info?.refundableBalance)],
                        disabled: a=> a.allAmount,
                        editor: {
                        }
                    })}
                    {bld.singleSelector(a => a.reason, {
                        validators: [required],
                        editor: {
                            itemsSource: enumItemsSource(RefundCardReason)
                        }
                    })}
                    {bld.singleSelector(a => a.paymentMethod, {
                        validators: [required],
                        style: "vertical-label",
                        label: "refund-method",
                        editor: {
                            template: SingleSelectorTemplates.Buttons,
                            style: ["horizontal", "big"], 
                            itemsSource: staticItemsSourceWithIcon(
                                ["cash", PaymentMethod.Cash, <MaterialIcon name="money" style="green" />],
                                ["bank-transfer", PaymentMethod.BankTransfer, <MaterialIcon name="account_balance" />]
                            )
                        }
                    })}
                    {bld.text(a => a.providerTransId, {
                        visible: a => a.paymentMethod == PaymentMethod.BankTransfer,
                    })}
                    {bld.text(a => a.note, {
                        editor: {
                            multiLine: true,
                            rows: 3
                        }
                    })}
                    {bld.boolean(a => a.reset, {
                        label: "reset-the-card",
                        onChanged: (m, v) => {
                            if (!v)
                                m.close = false;
                        }
             
                    })}

                    {bld.boolean(a => a.close, {

                        label: "close-wallet",
                        disabled: v=> !v.reset
                    })}

                    {bld.when(a => a.close, <Message type="warning">
                        {formatText("warn-close-wallet") }
                    </Message>)}

                </>, {
                    value: m.data,
                    style: "vertical",
                    commitMode: "auto-inplace",
                    inputField: {
                        style: "filled"
                    },
                })}
            </>),
        });
    }

    async refundAsync() {

        if (!await this.editor.commitAsync())
            return;

        const orderId = await executeAsync("CreateOrder", {
            type: OrderType.CardRefund,
            operatorId: context.operator?.id,
            salePointId: context.salePoint?.id,
            note: this.data.note,
            refundReason: this.data.reason,
            items: [{
                quantity: 1,
                unitAmount: this.data.amount,
                description: formatText("refund") as string,
                productId: undefined,
                formatId: undefined,
                type: OrderItemType.Amount,
                vatPercentage: 0
            }]
        });

        if (!orderId)
            return;

        const auth = await executeAsync("CreatePayment", {
            orderId,
            salePointId: context.salePoint.id,
            operatorId: context.operator?.id,
        });

        if (!auth)
            return;

        const card = await handleError(() => paymentService.readCardAsync({
            payToken: auth,
            message: formatText("msg-wait-for-card-refund") as string,
            amount: this.data.amount
        }));

        if (!card)
            return;

        if (!card?.payAuth) {
            userInteraction.messageAsync(formatText("err-pay-token"), "error", 3000);
            return;
        }

        const payRes = await useNetwork(() =>
            withSignManager(cid =>
                executeAsync("PayOrder", {
                    connectionId: cid,
                    cardToken: card.payAuth,
                    orderId: orderId,
                    method: this.data.paymentMethod,
                    salePointId: context.salePoint.id,
                    operatorId: context.operator?.id,
                    external: {
                        transactionId: this.data.providerTransId,
                        name: "BankTransfer"
                    }
                })));


        if (payRes.errorMessage) {
            userInteraction.messageAsync(formatText(payRes.errorMessage), "error", 3000);
            return;
        }

        if (this.data.reset) {

            const info = await executeAsync("GetCardInfo", { cardCode: this.code });

            await executeAsync("ResetCard", {
                cardId: info.cardId,
                salePointId: context.salePoint?.id, 
                closeWallet: this.data.close
            })
        }
        
        userInteraction.messageAsync(formatText("msg-refund-success"), "success", 3000);

        this.host.closeAsync(true);
    }

    async showErrorAsync(msg: string, extraToFix?) {

        const body = <MarkDown content={msg} />

        await userInteraction.messageBoxAsync(body, "error", MessageBoxButton.Ok)
        return false;
    }

    override async onLoadAsync(args: IRefundCardPageArgs) {

        if (!args.cardCode) {
            const card = await paymentService.readCardAsync({
                message: "msg-wait-for-card-verify"
            });
            if (!card?.code)
                return false;
            args.cardCode = card.code;
        }

        this.code = args.cardCode;

        this.info = await executeAsync("GetCardInfo", { cardCode: this.code });

        if (!this.info)
            return false;

        if (this.info.cardStatus == PaymentCardStatus.NotActive)
            return await this.showErrorAsync("err-card-not-active");

        if (this.info.cardStatus == PaymentCardStatus.Blocked) {

            if (!await userInteraction.confirmAsync(formatText("msg-card-blocked-warning"), "confirm")) 
                return false;

            const unblockRes = await executeAsync("UnblockCard", {
                cardId: this.info.cardId,
                salePointId: context?.salePoint?.id
            });

            if (!unblockRes)
                return false;
        }

        if (this.info.refundableBalance <= 0) {
            return await this.showErrorAsync(formatText("err-card-not-refundable-balance",
                formatCurrency(this.info.balance),
                formatCurrency(this.info.refundableBalance)) as string);
        }

        this.data.amount = this.info?.refundableBalance;

        return true;
    }

    editor: ObjectEditor<IRefundData>;

    code: string;

    info: IPaymentCardInfo;

    maxAmount = 0;

    data = {
        allAmount: true,
        paymentMethod: PaymentMethod.Cash,
        reset: true
    } as IRefundData;


    static override info = contentInfo({
        name: "refund-card",
        route: "/cards/refund/{cardCode}",
        factory: () => new RefundCardPage(),
        icon: <MaterialIcon style="red" name="arrow_circle_down" />,
        features: [
            Authorize(bld => bld.businessAccount().resource("PaymentCard", "write")),
            WithContext("sale-point", SalePointType.Charge)
        ]
    }) as IContentInfo;
}

registerComponent(RefundCardPage, "RefundCardPage");